/**
 * Tasks domain API (Sprint Calendar T1).
 *
 * Targets the org-scoped `/api/admin/tasks` endpoints. Mirrors the
 * `operationalDocuments.js` style: thin fetch wrappers that parse the
 * admin error envelope. Response shapes match the backend Pydantic
 * schemas in `app/schemas/task.py`.
 *
 * Endpoints:
 *   POST   /api/admin/tasks                        TaskRead (201)
 *   GET    /api/admin/tasks                        { items, total }
 *   GET    /api/admin/tasks/{id}                   TaskRead
 *   PATCH  /api/admin/tasks/{id}                   TaskRead
 *   POST   /api/admin/tasks/{id}/complete          TaskRead
 *   POST   /api/admin/tasks/{id}/reopen            TaskRead   body optional
 *   POST   /api/admin/tasks/{id}/start             TaskRead   (T2.4.3, strict)
 *   POST   /api/admin/tasks/{id}/pause             TaskRead   (T2.4.3, strict)
 *   POST   /api/admin/tasks/{id}/block             TaskRead   (T2.4.3, strict)
 *   POST   /api/admin/tasks/{id}/unblock           TaskRead   (T2.4.3, strict)
 *   DELETE /api/admin/tasks/{id}                   204 No Content
 *   GET    /api/admin/tasks/{id}/attachments       { items }  (TASK-UX1)
 *   POST   /api/admin/tasks/{id}/attachments       multipart, 201
 *   GET    /api/admin/tasks/{id}/attachments/{aid}/download  { url }
 *   DELETE /api/admin/tasks/{id}/attachments/{aid} 204 No Content
 *
 * The four strict-workflow endpoints (start/pause/block/unblock) return
 * 409 on invalid current-status (unlike complete/reopen which are
 * idempotent). Callers must surface the parsed error message to the user
 * — the backend builds a localized German "Aktion 'X' ist im aktuellen
 * Status 'Y' nicht erlaubt." detail string which is safe to display as-is.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  getApiHeadersMultipart,
  parseAdminErrorResponse,
  adminErrorFrom,
} from "./core";

function _qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      value
        .filter((v) => v != null && v !== "")
        .forEach((v) => sp.append(key, String(v)));
    } else if (value instanceof Date) {
      sp.set(key, value.toISOString());
    } else {
      sp.set(key, String(value));
    }
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/**
 * GET /api/admin/tasks
 * Rows carry ``participant_user_ids`` and ``participant_count`` (ASSIGN1)
 * — ids and a number, never the people. The calendar's "Nur meine"
 * filter reads the ids; the list shows the count as a badge. Names live
 * only on the detail response, which is the only shape that needs them.
 *
 * @param {{
 *   status?: string[],
 *   assigned_to_user_id?: string,
 *   task_type?: string[],
 *   priority?: string[],
 *   due_from?: string|Date,
 *   due_to?: string|Date,
 *   tenant_id?: string,
 *   application_id?: string,
 *   unit_id?: string,
 *   landlord_id?: string,
 *   tenancy_id?: string,
 *   q?: string,
 *   sort?: "picker_relevance",
 *   limit?: number,
 *   offset?: number,
 * }} [params]
 *
 * ``q`` (C1) is a case-insensitive substring search on the task title,
 * trimmed server-side; an empty value behaves like no search.
 * ``sort="picker_relevance"`` orders open tasks first, then done, then
 * cancelled, by due date inside each bucket. Omitting ``sort`` keeps
 * the historical due_at ASC NULLS LAST, created_at DESC order.
 *
 * @returns {Promise<{ items: Array<object>, total: number }>}
 */
export function fetchAdminTasks(params = {}) {
  return fetch(
    `${API_BASE_URL}/api/admin/tasks${_qs(params)}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    // RESP2e: `adminErrorFrom` instead of a bare Error, so the list can
    // tell an invalid area filter (a stale link, a deleted area) apart
    // from a network failure and offer to clear the filter rather than
    // just printing a sentence. Message text is unchanged.
    if (!res.ok) throw await adminErrorFrom(res);
    return res.json();
  });
}

/**
 * GET /api/admin/tasks/{taskId}
 *
 * Returns the single TaskRead for the given id; throws if the row is
 * missing, in another org, or any non-2xx response (matches the
 * parseAdminErrorResponse convention of the sibling list helper).
 */
export function fetchAdminTaskById(taskId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/** POST /api/admin/tasks — TaskCreate body. Returns TaskRead. */
export async function createAdminTask(payload) {
  const res = await fetch(`${API_BASE_URL}/api/admin/tasks`, {
    method: "POST",
    headers: { ...getApiHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  // RESP2d: keeps `err.code` so a stale category can be told
  // apart from an unknown one. Message unchanged.
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/** PATCH /api/admin/tasks/{id} — TaskUpdate body. Returns TaskRead. */
export async function updateAdminTask(taskId, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}`,
    {
      method: "PATCH",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  // RESP2d: keeps `err.code` so a stale category can be told
  // apart from an unknown one. Message unchanged.
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/** POST /api/admin/tasks/{id}/complete — no body. Returns TaskRead. */
export async function completeAdminTask(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/complete`,
    { method: "POST", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * POST /api/admin/tasks/{id}/reopen
 * @param {string} taskId
 * @param {"todo"|"in_progress"|"cancelled"} [newStatus="todo"]
 * @returns {Promise<object>} TaskRead
 */
export async function reopenAdminTask(taskId, newStatus = "todo") {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/reopen`,
    {
      method: "POST",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ new_status: newStatus }),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * POST /api/admin/tasks/{id}/start — strict transition todo|blocked → in_progress.
 * Throws on 409 with the backend's German detail message attached to err.message.
 * ``err.status`` carries the HTTP code so callers can distinguish 409 (toast)
 * from 404 (stale row) and others.
 */
export async function startAdminTask(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/start`,
    { method: "POST", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** POST /api/admin/tasks/{id}/pause — strict transition in_progress → todo. */
export async function pauseAdminTask(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/pause`,
    { method: "POST", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** POST /api/admin/tasks/{id}/block — strict transition todo|in_progress → blocked. */
export async function blockAdminTask(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/block`,
    { method: "POST", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** POST /api/admin/tasks/{id}/unblock — strict transition blocked → todo. */
export async function unblockAdminTask(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/unblock`,
    { method: "POST", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** DELETE /api/admin/tasks/{id} */
export async function deleteAdminTask(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) {
    // SAFE-DELETE1: 409 carries the work-report count in its German
    // detail — the status lets callers style that case distinctly.
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return { ok: true };
}

/**
 * GET /api/admin/tasks/{taskId}/comments — T2.3.4.
 *
 * Returns ``{ comments: TaskCommentRead[], total: number }`` with each
 * comment carrying ``user_email`` and ``user_full_name`` enrichment
 * already populated by the route layer.
 *
 * On non-2xx the thrown Error carries ``err.status`` (the HTTP code) in
 * addition to the parsed message, so callers can distinguish 404
 * (handled inline by ``TaskCommentsSection``) from 5xx (toast).
 * The shared ``parseAdminErrorResponse`` helper still only returns a
 * string — the status is attached here in the call site, not in the
 * shared helper, so the change is strictly additive.
 */
export async function fetchAdminTaskComments(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/comments`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * GET /api/admin/tasks/{taskId}/activity — T2.4.1.
 *
 * Returns ``{ items: TaskActivityRead[], total: number }``, newest
 * first, capped at 100 by the route. Each item carries
 * ``actor_user_email`` + ``actor_user_full_name`` enrichment populated
 * by the route layer; unknown actors leave those fields ``null``.
 *
 * Same status-attached-to-Error contract as ``fetchAdminTaskComments``
 * so ``TaskActivitySection`` can route 404 → inline retry and
 * everything else → toast.
 */
export async function fetchAdminTaskActivity(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/activity`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * POST /api/admin/tasks/{taskId}/comments — T2.3.4.
 *
 * Body shape: ``{ body }``. Returns the newly-created
 * ``TaskCommentRead`` with the author's email + full_name already
 * inlined by the route. Same status-attached-to-Error contract as
 * ``fetchAdminTaskComments``.
 */
export async function createAdminTaskComment(taskId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/comments`,
    {
      method: "POST",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * PATCH /api/admin/tasks/{taskId}/comments/{commentId} — T2.4.2.
 *
 * Author-only on the backend (403 if non-author tries). Returns the
 * updated TaskCommentRead with author enrichment. Same
 * status-attached-to-Error contract as the sibling helpers.
 *
 * Note: the backend short-circuits on no-op edits (incoming body
 * matches stored body after trim) — no audit row written,
 * ``updated_at`` untouched. The response is still 200 with the
 * unchanged comment.
 */
export async function updateAdminTaskComment(taskId, commentId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}`,
    {
      method: "PATCH",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * DELETE /api/admin/tasks/{taskId}/comments/{commentId} — T2.4.2.
 *
 * Author-only on the backend (403 if non-author tries). Returns
 * 204 No Content on success; this helper returns a sentinel
 * ``{ ok: true }`` so callers can distinguish "no body" from "no
 * response". Same status-attached-to-Error contract as the sibling
 * helpers.
 */
export async function deleteAdminTaskComment(taskId, commentId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return { ok: true };
}

// ── Task attachments (TASK-UX1) ───────────────────────────────────────
//
// Files live in R2; the backend never hands out the object key, so a
// download is TWO calls: ask for a short-lived signed URL, then follow
// it. That is why there is no ``downloadUrl`` string to build here —
// the URL does not exist until the server signs it.

/**
 * GET /api/admin/tasks/{taskId}/attachments
 *
 * @returns {Promise<Array<object>>} newest first, each with
 *   ``{ id, task_id, file_name, content_type, size_bytes, created_at,
 *      uploaded_by_user_id, uploaded_by_name }``. No ``object_key``.
 */
export async function fetchAdminTaskAttachments(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/attachments`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  return Array.isArray(data?.items) ? data.items : [];
}

/**
 * POST /api/admin/tasks/{taskId}/attachments — multipart, one file.
 *
 * The backend enforces type and size; a rejection comes back as 400 with
 * a German detail, which the caller shows verbatim rather than guessing
 * its own reason.
 */
export async function uploadAdminTaskAttachment(taskId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}/attachments`,
    { method: "POST", headers: getApiHeadersMultipart(), body: fd }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * GET /api/admin/tasks/{taskId}/attachments/{id}/download
 *
 * @returns {Promise<{ url: string }>} valid for minutes, not forever.
 */
export async function fetchAdminTaskAttachmentDownloadUrl(taskId, attachmentId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}` +
      `/attachments/${encodeURIComponent(attachmentId)}/download`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * DELETE /api/admin/tasks/{taskId}/attachments/{id} — 204 No Content.
 *
 * Returns ``{ ok: true }`` so a caller can tell "empty body" from "no
 * response", matching the comment helpers above.
 */
export async function deleteAdminTaskAttachment(taskId, attachmentId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(taskId)}` +
      `/attachments/${encodeURIComponent(attachmentId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return { ok: true };
}
